import produce from "immer";
import create from "zustand";
import { Artist, ArtistId } from "../../models/Artist";
import { ArtistRef } from "../../models/Directory";
import { ArtistService } from "../../services/ArtistService";
import { DirectoryService } from "../../services/DirectoryService";
import { RegionId } from "../../models/Region";

export type ArtistDirectory = {
  cursor: number;
  pageSize: number;
  artists: Record<ArtistId, Artist | ArtistRef>;
  regionArtists: Record<RegionId, ArtistId[]>;
  queued: ArtistId[];
  fetching: ArtistId[];
  fetchingMeta: ArtistId[];
  empty: ArtistId[];
  meta: ArtistId[];
  all: ArtistId[];
  roster: ArtistId[];
  observing: boolean;
  rosterDone: boolean;
  topUnsubscribe: (() => void) | null;
  status: "idle" | "fetching" | "initial";
  fetch: (id: string) => void;
  fetchRegion: (id: RegionId) => void;
  fetchMeta: (id: string) => void;
  shouldFetch: (id: string) => boolean;
  shouldFetchMeta: (id: string) => boolean;
  fetchAll: () => void;
  retrieve: (id: ArtistId) => ArtistRef | undefined;
  observeDirectory: () => void;
  fetchNextRosterPage: () => Promise<void>;
  observeTopRoster: (pageSize: number) => void;
  stopObservingTopRoster: () => void;
};

export const useArtists = create<ArtistDirectory>((set, get) => ({
  observing: false,
  artists: {},
  fetching: [],
  regionArtists:{},
  roster: [],
  fetchingMeta: [],
  meta: [],
  status: "initial",
  empty: [],
  queued: [],
  all: [],
  cursor: 1,
  pageSize: 20,
  rosterDone: false,
  topUnsubscribe: null,
  fetchRegion: async (regionId) => {
    const { regionArtists, pageSize } = get();
    
    // Set status to fetching
    set({ status: "fetching" });
    
    try {
      // Fetch artists for the region with pagination
      const artists = await ArtistService.fetchArtistsForRegion(
        regionId,
        pageSize,
        (regionArtists[regionId] || []).length
      );

      // Update the store with the new artists
      const nextState = produce(get(), (draftState) => {
        // Initialize region array if it doesn't exist
        if (!draftState.regionArtists[regionId]) {
          draftState.regionArtists[regionId] = [];
        }

        // Add new artists to both the regions record and artists record
        artists.forEach(artist => {
          // Add to regions array
          draftState.regionArtists[regionId].push(artist.id);
          
          // Add to artists record
          draftState.artists[artist.id] = artist;
          
          // Add to all artists if not already present
          if (!draftState.all.includes(artist.id)) {
            draftState.all.push(artist.id);
          }
        });

        // Set status back to idle
        draftState.status = "idle";
      });

      set(nextState);
    } catch (error) {
      // Handle error case
      set({ status: "idle" });
      console.error(`Error fetching artists for region ${regionId}:`, error);
    }
  },
  observeDirectory: () => {
    // Legacy entry point: kick off paginated fetch instead of downloading
    // the whole directory. Callers that just want the first page get it.
    const { observing } = get();
    if (observing) return;
    set({ observing: true });
    get().fetchNextRosterPage();
  },
  fetchNextRosterPage: async () => {
    const { roster, artists, pageSize, rosterDone, status } = get();
    if (rosterDone || status === "fetching") return;
    set({ status: "fetching" });

    const last = roster[roster.length - 1];
    const lastSongs = last ? (artists[last] as ArtistRef | undefined)?.songs : undefined;

    try {
      const page = await DirectoryService.fetchPage(pageSize, lastSongs);
      set(
        produce(get(), (draft) => {
          for (const artist of page) {
            if (!draft.roster.includes(artist.id)) {
              draft.roster.push(artist.id);
            }
            if (!draft.all.includes(artist.id)) {
              draft.all.push(artist.id);
            }
            draft.artists[artist.id] = {
              ...(draft.artists[artist.id] || {}),
              ...artist,
            };
          }
          draft.rosterDone = page.length < pageSize;
          draft.status = "idle";
        })
      );
    } catch (err) {
      console.error("[useArtists] fetchNextRosterPage error", err);
      set({ status: "idle" });
    }
  },
  observeTopRoster: (pageSize) => {
    const existing = get().topUnsubscribe;
    if (existing) existing();
    const unsub = DirectoryService.observeTop(pageSize, (top) => {
      set(
        produce(get(), (draft) => {
          for (const artist of top) {
            draft.artists[artist.id] = {
              ...(draft.artists[artist.id] || {}),
              ...artist,
            };
          }
          // Re-rank the head of the roster to match the live snapshot,
          // preserving any later pages that were fetched via pagination.
          const topIds = top.map((a) => a.id);
          const tail = draft.roster.filter((id) => !topIds.includes(id));
          draft.roster = [...topIds, ...tail];
        })
      );
    });
    set({ topUnsubscribe: unsub });
  },
  stopObservingTopRoster: () => {
    const existing = get().topUnsubscribe;
    if (existing) existing();
    set({ topUnsubscribe: null });
  },
  retrieve: (id) => {
    const directory = get();
    const artist = directory.artists[id];
    return artist;
  },
  fetchAll: async () => {
    set({ status: "fetching" });
    const artists = await DirectoryService.fetchAll();
    const directoryWithEntryAdded = produce(get(), (draftState) => {
      for (const artist of artists) {
        if (!draftState.roster.includes(artist.id)) {
          draftState.roster.push(artist.id);
        }
        if (!draftState.all.includes(artist.id)) {
          draftState.all.push(artist.id);
        }
        draftState.artists[artist.id] = {
          ...artist,
          ...draftState.artists[artist.id],
        };
      }
    });
    set(directoryWithEntryAdded);
    set({ status: "idle" });
  },
  shouldFetch: (id) => {
    const directory = get();
    if (
      directory.all.includes(id) ||
      directory.fetching.includes(id) ||
      directory.queued.includes(id) ||
      directory.empty.includes(id)
    )
      return false;
    return true;
  },
  shouldFetchMeta: (id) => {
    const directory = get();
    if (
      directory.meta.includes(id) ||
      directory.fetchingMeta.includes(id) ||
      directory.queued.includes(id) ||
      directory.empty.includes(id)
    )
      return false;
    return true;
  },
  queue: (id: ArtistId) => {
    const directory = get();
    const directoryWithEntryInQueue = produce(directory, (draftState) => {
      draftState.queued.push(id);
    });
    set(directoryWithEntryInQueue);
  },
  queueMany: (ids: ArtistId[]) => {
    const directory = get();
    const directoryWithEntriesInQueue = produce(directory, (draftState) => {
      for (const artistId of ids) draftState.queued.push(artistId);
    });
    set(directoryWithEntriesInQueue);
  },
  fetch: async (id) => {
    if (!id) {
      return 0;
    }
    const directory = get();
    const directoryWithEntryInFetching = produce(directory, (draftState) => {
      draftState.fetching.push(id);
    });
    set(directoryWithEntryInFetching);
    //Initiate the Fetch for the nextQueue
    const artistRef = await DirectoryService.fetch(id);
    //Add Fetched Entries to directory
    const nextState = produce(get(), (draftState) => {
      draftState.artists[artistRef.id] = artistRef;
      draftState.fetching = draftState.fetching.filter(
        (x) => x !== artistRef.id
      );
      if (!draftState.all.includes(id)) draftState.all.push(id);
    });
    set(nextState);
  },
  fetchMeta: async (id) => {
    const directory = get();
    const directoryWithEntryInFetching = produce(directory, (draftState) => {
      draftState.fetching.push(id);
    });
    set(directoryWithEntryInFetching);
    try {
      //Initiate the Fetch for the nextQueue
      const artistRef = await ArtistService.fetch(id);
      //Add Fetched Entries to directory
      const nextState = produce(get(), (draftState) => {
        draftState.artists[artistRef.id] = artistRef;
        draftState.fetching = draftState.fetching.filter(
          (x) => x !== artistRef.id
        );
        if (!draftState.all.includes(id)) draftState.all.push(id);
      });
      set(nextState);
    } catch {
      const emptyState = produce(get(), (draftState) => {
        draftState.fetching = draftState.fetching.filter((x) => x !== id);
        if (!draftState.empty.includes(id)) draftState.empty.push(id);
      });
      set(emptyState);
    }
  },
}));
